import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { Company } from '../company/company.model';
import { TenantScope } from '../company/tenant-access.model';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { DashboardCompanyScopeSelectorComponent } from '../shared/page-layout/dashboard-company-scope-selector.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../services/session-storage.service';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';
import { AccountsService } from './accounts.service';
import { TenantScopeService } from '../company/tenant-scope.service';
import { Subscription } from 'rxjs';
import {
    AccountsOverviewDashboard,
    ActionQueueItem,
    DashboardChart,
    DashboardKpi,
    LogisticsProfitabilityRow
} from './accounts-dashboard.model';

@Component({
    selector: 'app-accounts',
    templateUrl: './accounts.component.html',
    styleUrls: ['./accounts.component.css'],
    standalone: true,
    providers: [MessageService],
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        ChartModule,
        DatePickerModule,
        ProgressSpinnerModule,
        TableModule,
        TagModule,
        ToastModule,
        DashboardCompanyScopeSelectorComponent,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class AccountsComponent implements OnInit, OnDestroy {
    dashboard?: AccountsOverviewDashboard;
    loading = false;
    loadError = '';
    startDate = new Date(new Date().getFullYear(), new Date().getMonth() - 5, 1);
    endDate = new Date();
    currencyCode = 'SAR';

    readonly chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 150,
        animation: false,
        plugins: {
            legend: {
                display: true,
                position: 'bottom'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                }
            }
        }
    };

    readonly doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 150,
        animation: false,
        plugins: {
            legend: {
                display: true,
                position: 'bottom'
            }
        }
    };

    canViewReports = false;
    canViewInvoices = false;
    canViewBills = false;
    canViewVouchers = false;
    companyId: number | null = null;
    companyGroupId: number | null = null;
    activeGroupName = '';
    groupCompanies: Company[] = [];
    selectedGroupCompanyId: number | null = null;
    private scopeSubscription?: Subscription;
    private readonly chartDataCache = new WeakMap<DashboardChart, any>();

    constructor(
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private tenantScopeService: TenantScopeService,
        private permissionService: PermissionService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.canViewReports = this.permissionService.hasPermission(app_permissions.accounts.reports.view, 'ANY');
        this.canViewInvoices = this.permissionService.hasPermission(app_permissions.accounts.invoice.view);
        this.canViewBills = this.permissionService.hasPermission(app_permissions.accounts.bill.view);
        this.canViewVouchers = this.permissionService.hasPermission(app_permissions.accounts.voucher.view);
        this.currencyCode = this.detectCurrency();
        this.scopeSubscription = this.tenantScopeService.activeScope$.subscribe(scope => this.handleScopeChange(scope));
    }

    ngOnDestroy(): void {
        this.scopeSubscription?.unsubscribe();
    }

    load(): void {
        const companyId = this.companyGroupId
            ? undefined
            : (this.companyId ?? this.sessionStorageService.getCompany()?.id);
        const companyGroupId = this.companyGroupId ?? undefined;
        const memberCompanyId = companyGroupId ? (this.selectedGroupCompanyId ?? undefined) : undefined;
        if (!companyId && !companyGroupId) {
            this.loadError = 'Company or group context is missing. Select a scope and refresh the dashboard.';
            return;
        }

        this.loading = true;
        this.loadError = '';
        this.accountsService.getOverviewDashboard({
            owningCompanyId: companyId,
            companyGroupId,
            memberCompanyId,
            startDate: this.toApiDate(this.startDate),
            endDate: this.toApiDate(this.endDate),
            currencyCode: this.currencyCode
        }).subscribe({
            next: dashboard => {
                this.dashboard = dashboard ?? {};
                this.currencyCode = dashboard?.currencyCode || this.currencyCode;
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.loadError = err?.error?.message || 'Finance dashboard could not be loaded.';
                this.messageService.add({ severity: 'error', summary: 'Dashboard failed', detail: this.loadError });
            }
        });
    }

    get companyName(): string {
        if (this.companyGroupId) {
            const selectedCompanyName = this.groupCompanies.find(company => company.id === this.selectedGroupCompanyId)?.name;
            return selectedCompanyName
                ? `${this.activeGroupName} | ${selectedCompanyName}`
                : `${this.activeGroupName} | All companies`;
        }
        const company = this.sessionStorageService.getCompany() as any;
        return company?.companyName || company?.name || company?.englishName || 'Current company';
    }

    get showGroupCompanySelector(): boolean {
        return !!this.companyGroupId && this.groupCompanies.length > 0;
    }

    get periodLabel(): string {
        return `${this.toApiDate(this.startDate)} to ${this.toApiDate(this.endDate)}`;
    }

    get refreshedLabel(): string {
        return this.dashboard?.refreshedAt ? new Date(this.dashboard.refreshedAt).toLocaleString() : 'Not loaded';
    }

    get headlineMetrics(): PageMetric[] {
        const executive = this.dashboard?.executiveKpis || {};
        const controls = this.dashboard?.controlKpis || {};
        return [
            { label: 'Open AR', value: this.money(executive['openAr']?.amount), hint: executive['openAr']?.hint, tone: this.metricTone(executive['openAr']) },
            { label: 'Open AP', value: this.money(executive['openAp']?.amount), hint: executive['openAp']?.hint, tone: this.metricTone(executive['openAp']) },
            { label: 'Gross margin', value: this.money(executive['grossMargin']?.amount), hint: executive['grossMargin']?.hint, tone: this.metricTone(executive['grossMargin']) },
            { label: 'Control exceptions', value: (controls['apMatching']?.count || 0) + (controls['einvoice']?.count || 0), hint: 'AP match + e-invoice failures', tone: ((controls['apMatching']?.count || 0) + (controls['einvoice']?.count || 0)) > 0 ? 'danger' : 'success' }
        ];
    }

    get executiveCards(): DashboardKpi[] {
        return this.kpiValues(this.dashboard?.executiveKpis);
    }

    get receivableCards(): DashboardKpi[] {
        return this.kpiValues(this.dashboard?.receivableKpis);
    }

    get payableCards(): DashboardKpi[] {
        return this.kpiValues(this.dashboard?.payableKpis);
    }

    get controlCards(): DashboardKpi[] {
        return this.kpiValues(this.dashboard?.controlKpis);
    }

    get logisticsRows(): LogisticsProfitabilityRow[] {
        return this.dashboard?.logisticsProfitability || [];
    }

    get queueSections(): Array<{ key: string; title: string; rows: ActionQueueItem[] }> {
        const queues = this.dashboard?.actionQueues || {};
        return [
            { key: 'overdueInvoices', title: 'Overdue Customer Invoices', rows: queues['overdueInvoices'] || [] },
            { key: 'paymentRun', title: 'Payment Run Candidates', rows: queues['paymentRun'] || [] },
            { key: 'apExceptions', title: 'AP Match Exceptions', rows: queues['apExceptions'] || [] },
            { key: 'zatcaFailures', title: 'Failed ZATCA Documents', rows: queues['zatcaFailures'] || [] },
            { key: 'bankReconciliation', title: 'Bank Reconciliation Exceptions', rows: queues['bankReconciliation'] || [] },
            { key: 'workforceCash', title: 'Workforce Cash Exposure', rows: queues['workforceCash'] || [] }
        ];
    }

    get chartEntries(): Array<{ key: string; chart: DashboardChart }> {
        const charts = this.dashboard?.charts || {};
        return ['profitabilityTrend', 'aging', 'cashMovement', 'paymentCalendar', 'zatcaStatus', 'bankReconciliation']
            .filter(key => !!charts[key])
            .map(key => ({ key, chart: charts[key] }));
    }

    chartData(chart: DashboardChart): any {
        const cached = this.chartDataCache.get(chart);
        if (cached) {
            return cached;
        }
        const value = {
            labels: chart.labels || [],
            datasets: (chart.datasets || []).map(dataset => ({
                label: dataset.label,
                data: dataset.data || [],
                backgroundColor: this.chartColor(dataset.backgroundColor || '#0f766e', chart.type),
                borderColor: dataset.borderColor || dataset.backgroundColor || '#0f766e',
                fill: dataset.fill ?? false,
                tension: 0.35
            }))
        };
        this.chartDataCache.set(chart, value);
        return value;
    }

    chartType(chart: DashboardChart): any {
        return chart.type || 'bar';
    }

    chartOptionsFor(chart: DashboardChart): any {
        return chart.type === 'doughnut' || chart.type === 'pie' ? this.doughnutOptions : this.chartOptions;
    }

    kpiDisplay(kpi: DashboardKpi): string {
        if (kpi.amount !== undefined && kpi.amount !== null) {
            return this.money(kpi.amount, kpi.currencyCode);
        }
        return `${kpi.count ?? 0}`;
    }

    money(amount?: number | null, currency = this.currencyCode): string {
        return `${currency || this.currencyCode} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    percent(value?: number | null): string {
        return `${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const value = (status || '').toUpperCase();
        if (['POSTED', 'PAID', 'MATCHED', 'CLEARED', 'REPORTED', 'RELEASED', 'SUCCESS'].includes(value)) return 'success';
        if (['PENDING', 'DRAFT', 'IN_REVIEW', 'NOT_PROPOSED'].includes(value)) return 'info';
        if (['PAYMENT_HELD', 'ON_HOLD', 'PARTIAL', 'WARNING'].includes(value)) return 'warn';
        if (['FAILED', 'MATCH_EXCEPTION', 'OVERDUE', 'ERROR', 'UNRECONCILED'].includes(value)) return 'danger';
        return 'secondary';
    }

    cardClass(kpi?: DashboardKpi): string {
        return `kpi-card ${kpi?.severity || 'default'}`;
    }

    openRoute(route?: string): void {
        if (!route) return;
        this.router.navigateByUrl(route);
    }

    exportSnapshot(): void {
        const payload = {
            company: this.companyName,
            period: this.periodLabel,
            refreshedAt: this.dashboard?.refreshedAt,
            executiveKpis: this.dashboard?.executiveKpis,
            receivableKpis: this.dashboard?.receivableKpis,
            payableKpis: this.dashboard?.payableKpis,
            controlKpis: this.dashboard?.controlKpis
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `finance-command-center-${this.toApiDate(new Date())}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    onDashboardCompanyScopeChange(companyId: number | null): void {
        this.selectedGroupCompanyId = companyId;
        this.currencyCode = this.detectCurrency();
        this.load();
    }

    private kpiValues(map?: Record<string, DashboardKpi>): DashboardKpi[] {
        return Object.values(map || {});
    }

    private metricTone(kpi?: DashboardKpi): PageMetric['tone'] {
        const severity = kpi?.severity || 'default';
        return severity === 'default' ? 'default' : severity;
    }

    private chartColor(color: string, type?: string): any {
        if (type === 'doughnut' || type === 'pie') {
            return ['#0f766e', '#0369a1', '#b45309', '#b91c1c', '#64748b', '#7c3aed'];
        }
        return color;
    }

    private detectCurrency(): string {
        if (this.companyGroupId) {
            const scopedCompany = this.selectedGroupCompanyId
                ? this.groupCompanies.find(company => company.id === this.selectedGroupCompanyId)
                : this.groupCompanies.find(company => company?.countryId);
            const countryId = scopedCompany?.countryId;
            const country = this.sessionStorageService.getCountryList()?.find(item => item.id === countryId);
            return country?.currencyCode || 'SAR';
        }
        const company = this.sessionStorageService.getCompany();
        const country = this.sessionStorageService.getCountryList()?.find(item => item.id === company?.countryId);
        return country?.currencyCode || 'SAR';
    }

    private handleScopeChange(scope: TenantScope | null): void {
        const nextCompanyId = scope?.type === 'COMPANY' ? scope.companyId : null;
        const nextCompanyGroupId = scope?.type === 'GROUP' ? scope.companyGroupId : null;
        this.activeGroupName = scope?.type === 'GROUP' ? scope.companyGroupName : '';
        this.groupCompanies = scope?.type === 'GROUP' ? (scope.memberCompanies ?? []) : [];
        if (!this.groupCompanies.some(company => company.id === this.selectedGroupCompanyId)) {
            this.selectedGroupCompanyId = null;
        }
        if (this.companyId === nextCompanyId && this.companyGroupId === nextCompanyGroupId) {
            this.currencyCode = this.detectCurrency();
            this.load();
            return;
        }
        this.companyId = nextCompanyId;
        this.companyGroupId = nextCompanyGroupId;
        this.selectedGroupCompanyId = null;
        this.currencyCode = this.detectCurrency();
        this.load();
    }

    private toApiDate(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
}
